// TEN17 (docs/build-briefs/TEN17-brand-settings-and-theme.md; DEC-117, DEC-338, DEC-342 Q5): the pure decisions
// behind a member user's optional brand color scheme in My Profile. No DOM, no fetch: test/ten17-brand-ui-model.test.ts
// proves it. The brand scheme is one EXTRA option after the shipped schemes, offered only when the user's tenant
// belongs to a network that has saved brand settings, and it is never chosen for anyone: a stored `brand:` id
// that is not currently offered falls back to the product default.

import { BRAND_FONT_IDS, BRAND_THEME_ID_PREFIX, isSafeBrandTokens, networkIdFromBrandThemeId, type BrandTokens } from "../../src/domain/brandTheme.ts";
import { SYSTEM_FONT_ID, uploadedFontIdOf } from "../../src/domain/brandFontCatalog.ts";

/** What GET /tenants/:tenantId/me/network-brand returns for a member of a network with a brand. */
export interface MemberBrand {
  themeId: string;
  /** "<network name> brand". */
  label: string;
  version: number;
  tokens: BrandTokens;
  /** TEN17-F5: the font id the brand uses (a free font, or `upload:<uuid>`). */
  font: string;
  /** TEN17-F5: when the brand font is an uploaded one, its id, hash and format (the bytes come from the font
   * route), else null. */
  fontFile: MemberFontFile | null;
  /** TEN17-F1: the network's logo (hash and size only; the bytes come from the logo route), or null. */
  logo: MemberLogo | null;
}

export interface MemberFontFile {
  id: string;
  sha256: string;
  format: "woff2" | "woff" | "ttf" | "otf";
}

/** The uploaded font's descriptor, or null when it is absent or malformed (a malformed one is dropped, never fetched). */
function parseMemberFontFile(value: unknown): MemberFontFile | null {
  if (typeof value !== "object" || value === null) return null;
  const v = value as Record<string, unknown>;
  if (
    typeof v["id"] !== "string" ||
    uploadedFontIdOf(`upload:${v["id"]}`) === null ||
    typeof v["sha256"] !== "string" ||
    !/^[0-9a-f]{64}$/.test(v["sha256"]) ||
    !(v["format"] === "woff2" || v["format"] === "woff" || v["format"] === "ttf" || v["format"] === "otf")
  ) {
    return null;
  }
  return { id: v["id"], sha256: v["sha256"], format: v["format"] };
}

export interface MemberLogo {
  sha256: string;
  width: number;
  height: number;
}

/** A logo descriptor, or null when it is absent or malformed (a malformed one is dropped, never shown). */
function parseMemberLogo(value: unknown): MemberLogo | null {
  if (typeof value !== "object" || value === null) return null;
  const v = value as Record<string, unknown>;
  if (
    typeof v["sha256"] !== "string" ||
    !/^[0-9a-f]{64}$/.test(v["sha256"]) ||
    !Number.isInteger(v["width"]) ||
    !Number.isInteger(v["height"])
  ) {
    return null;
  }
  return { sha256: v["sha256"], width: v["width"] as number, height: v["height"] as number };
}

/** The reply body, or null when it is not a well formed `{ brand }`. A malformed brand is dropped, never applied. */
export function parseMemberBrandReply(body: unknown): { brand: MemberBrand | null } | null {
  if (typeof body !== "object" || body === null) return null;
  const brand = (body as Record<string, unknown>)["brand"];
  if (brand === null) return { brand: null };
  if (typeof brand !== "object" || brand === undefined) return null;
  const v = brand as Record<string, unknown>;
  if (
    typeof v["themeId"] !== "string" ||
    networkIdFromBrandThemeId(v["themeId"]) === null ||
    typeof v["label"] !== "string" ||
    v["label"].trim() === "" ||
    typeof v["version"] !== "number" ||
    !isSafeBrandTokens(v["tokens"])
  ) {
    return null;
  }
  // A reply from a server older than TEN17-F5 names no font: the colors and the font stacks still apply, and no
  // font stylesheet is linked. A font that is named but is not on the list drops the whole brand.
  const font = v["font"] === undefined ? SYSTEM_FONT_ID : v["font"];
  if (typeof font !== "string" || !(BRAND_FONT_IDS.includes(font) || uploadedFontIdOf(font) !== null)) return null;
  const fontFile = parseMemberFontFile(v["fontFile"]);
  // An uploaded brand font is fetched only when its descriptor matches the font id; otherwise the brand shows its
  // fallback stack (the tokens already carry one).
  const usable = fontFile !== null && font === `upload:${fontFile.id}` ? fontFile : null;
  return {
    brand: {
      themeId: v["themeId"],
      label: v["label"],
      version: v["version"],
      tokens: v["tokens"],
      font,
      fontFile: usable,
      logo: parseMemberLogo(v["logo"]),
    },
  };
}

export interface ThemeChoice {
  id: string;
  label: string;
}

/** The Color scheme list: the shipped schemes in their fixed order, then the brand scheme if it is offered. */
export function themeChoices(shipped: readonly ThemeChoice[], brand: MemberBrand | null): ThemeChoice[] {
  const list = shipped.map((option) => ({ id: option.id, label: option.label }));
  if (brand !== null) list.push({ id: brand.themeId, label: brand.label });
  return list;
}

export type ThemeResolution =
  | { kind: "shipped"; id: string }
  | { kind: "brand"; brand: MemberBrand };

/** Which theme to apply for a stored or chosen id. A `brand:` id applies only when it is exactly the scheme on
 * offer now; otherwise (not a member any more, brand removed, a stale id) the product default applies. */
export function resolveThemeId(themeId: string, brand: MemberBrand | null, defaultThemeId: string): ThemeResolution {
  if (!themeId.startsWith(BRAND_THEME_ID_PREFIX)) return { kind: "shipped", id: themeId };
  if (brand !== null && brand.themeId === themeId) return { kind: "brand", brand };
  return { kind: "shipped", id: defaultThemeId };
}

/** TEN17-F2 (DEC-427): the theme a signed-in user starts on. A saved choice wins, whatever it is; with none
 * (null), the brand on offer is the default; with no brand either, the product default. A saved `brand:` id is
 * returned as is and judged by `resolveThemeId`, which drops one that is not the scheme on offer. */
export function startingThemeId(saved: string | null, brand: MemberBrand | null, defaultThemeId: string): string {
  if (saved !== null) return saved;
  return brand !== null ? brand.themeId : defaultThemeId;
}

/** Whether the starting theme can change once the brand is known: only for a user who saved nothing, or whose
 * saved choice is a brand id. Any other saved scheme is already on screen and is left alone. */
export function followsBrandRead(saved: string | null): boolean {
  return saved === null || saved.startsWith(BRAND_THEME_ID_PREFIX);
}

/** TEN17-F4 (DEC-427): the picker's first option, "follow the default". Its value is empty because no theme id is
 * ever empty; choosing it saves NULL, which is what makes a user follow the network brand (or the product
 * default) again, including later brand changes and removal. */
export const FOLLOW_DEFAULT_VALUE = "";

/** The first option's label: the network's brand name plus "(default)", or "Default" with no brand. The brand label
 * is "<network name> brand"; the trailing word is dropped so the line reads "<network name> (default)". */
export function followDefaultLabel(brand: MemberBrand | null): string {
  if (brand === null) return "Default";
  const name = brand.label.replace(/ brand$/, "").trim();
  return `${name === "" ? brand.label : name} (default)`;
}

/** The Color scheme list as My Profile shows it: Default first, then the shipped schemes, then the brand. */
export function profileThemeChoices(shipped: readonly ThemeChoice[], brand: MemberBrand | null): ThemeChoice[] {
  return [{ id: FOLLOW_DEFAULT_VALUE, label: followDefaultLabel(brand) }, ...themeChoices(shipped, brand)];
}

/** What to save for a picker value: NULL for the Default option, else the id itself. */
export function savedThemeForPick(value: string): string | null {
  return value === FOLLOW_DEFAULT_VALUE ? null : value;
}

/** The picker value to show: Default while the user follows (nothing saved), else the scheme on screen. */
export function pickerValueFor(saved: string | null, showingId: string): string {
  return saved === null ? FOLLOW_DEFAULT_VALUE : showingId;
}
